import {
  AutoSave,
  Button,
  Card,
  DataStatus,
  Form,
  Label,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useEffect, useRef} from 'react';
import {useDispatch, useStore} from 'react-redux';

import {isNil} from 'ramda';
import {isNilOrEmpty, isString} from 'ramda-adjunct';

import {
  CreateMinipurchaseApiArg,
  interestApi,
  MinipurchaseResponseBody,
  UpdateMinipurchaseApiArg,
  useAddMinipurchaseOfferedVehicleMutation,
  useCreateMinipurchaseMutation,
  useUpdateMinipurchaseMutation,
} from '@dms/api/finance';
import {useGetAiLeadEmailMessageQuery} from '@dms/api/sales';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {interestRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  EmailLeadPreviewDialog,
  handleApiError,
  interestSearchParams,
  MessageText,
  queryParams,
  Section,
  useInterestEditPermissions,
  useTenant,
} from '@dms/shared';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  getSafely,
  handleFormReset,
  useNavigate,
  useQueryState,
} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {OfferedVehicles} from '../../components/OfferedVehicles';
import {useInterestData} from '../../hooks/useInterestData';
import {
  ExternalFilterConnector,
  ExternalFilterConnectorRef,
} from './components/ExternalFilterConnector';
import {ExternalFilterForm} from './components/ExternalFilterForm';
import {ExternalFilter} from './types/ExternalFilter';
import {buildMinipurchaseUpdateBody} from './utils/buildMinipurchaseUpdateBody';
import {transformExternalFilterToMinipurchaseSpecification} from './utils/transformExternalFilterToMinipurchaseSpecification';
import {transformMinipurchaseToExternalFilter} from './utils/transformMinipurchaseToExternalFilter';

interface SellingProps {
  interestId: string;
}

export function Selling(props: SellingProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();

  const [tab, setTab] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const {interest, offeredVehiclesList, miniPurchase, isActionsDisabled, isLoading, isError} =
    useInterestData(props.interestId);

  const {data: aiLeadEmailMessage} = useGetAiLeadEmailMessageQuery(
    {id: interest?.originLeadMessageId ?? ''},
    {
      skip: isNilOrEmpty(interest?.originLeadMessageId),
    }
  );

  // fixedCacheKey stores mutation state in the Redux store under a stable key,
  // allowing us to check if a mutation is in-flight via store.getState() — immune to stale closures
  // caused by AutoSave's debounced callbacks firing from old render cycles.
  const createMinipurchaseCacheKey = `create-minipurchase-${props.interestId}`;
  const [createMinipurchase] = useCreateMinipurchaseMutation({
    fixedCacheKey: createMinipurchaseCacheKey,
  });
  const [addMinipurchaseOfferedVehicle] = useAddMinipurchaseOfferedVehicleMutation();
  const updateMinipurchaseCacheKey = `update-minipurchase-${props.interestId}`;
  const [updateMinipurchase] = useUpdateMinipurchaseMutation({
    fixedCacheKey: updateMinipurchaseCacheKey,
  });

  const externalFilterRef = useRef<ExternalFilterConnectorRef>(null);
  const pendingMinipurchaseValuesRef = useRef<ExternalFilter | null>(null);
  const miniPurchaseRef = useRef(miniPurchase);

  const {hasChangeMinisalePriceOfferedInterestPermission} = useInterestEditPermissions({
    interestId: props.interestId,
  });

  const {tenantCurrency} = useTenant();

  useEffect(() => {
    miniPurchaseRef.current = miniPurchase;
  }, [miniPurchase]);

  useEffect(() => {
    if (
      !miniPurchase?.offeredVehiclesIds?.length &&
      tab === interestSearchParams.interestDetail.sellingTabs.offeredVehicles
    ) {
      setTab(interestSearchParams.interestDetail.sellingTabs.request);
    }
  }, [tab, miniPurchase?.offeredVehiclesIds?.length, setTab]);

  // Reads create mutation status directly from the Redux store at call time.
  // We cannot use useRef or closure-captured state because AutoSave's useDebouncedCallback
  // fires callbacks from stale closures (old render cycles), making refs unreliable.
  // Also checks 'fulfilled' status to guard the window between POST success and miniPurchase
  // query refetch completing — during which miniPurchase is still null.
  const isMinipurchaseCreateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    const mutationState = getSafely(mutations, createMinipurchaseCacheKey);
    return mutationState?.status === 'pending' || mutationState?.status === 'fulfilled';
  };

  // Reads update mutation status directly from the Redux store at call time.
  // Prevents concurrent PUT requests that would cause data loss — when a PUT is in-flight,
  // new values are queued in pendingMinipurchaseValuesRef and flushed after the PUT completes
  // using the server response as the base (avoiding stale miniPurchase from closures).
  const isMinipurchaseUpdateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    const mutationState = getSafely(mutations, updateMinipurchaseCacheKey);
    return mutationState?.status === 'pending';
  };

  // Recursively flushes values queued in pendingMinipurchaseValuesRef after each PUT completes.
  // Called after every successful update to drain any values that accumulated while the PUT was in-flight.
  const flushPendingValues = (base: MinipurchaseResponseBody) => {
    if (!pendingMinipurchaseValuesRef.current) {
      return;
    }

    const pendingValues = pendingMinipurchaseValuesRef.current;
    pendingMinipurchaseValuesRef.current = null;

    const nextArg: UpdateMinipurchaseApiArg = {
      interestId: props.interestId,
      body: buildMinipurchaseUpdateBody(base, pendingValues, tenantCurrency),
    };

    updateMinipurchase(nextArg)
      .unwrap()
      .then((response) => {
        externalFilterRef.current?.onChange(pendingValues);
        flushPendingValues(response);
      })
      .catch(handleApiError);
  };

  const onMiniPurchaseUpdate = (values: ExternalFilter) => {
    if (!miniPurchase || !miniPurchase?.id) {
      if (isMinipurchaseCreateInProgress()) {
        pendingMinipurchaseValuesRef.current = {...pendingMinipurchaseValuesRef.current, ...values};
        return;
      }

      const arg: CreateMinipurchaseApiArg = {
        body: {
          interestId: props.interestId,
          filteredSpecifications: transformExternalFilterToMinipurchaseSpecification(values),
          additionalInformation: values.additionalInformation ?? null,
        },
      };
      createMinipurchase(arg)
        .unwrap()
        .then(() => {
          dispatch(interestApi.util.invalidateTags([{type: 'Interest', id: props.interestId}]));
          externalFilterRef.current?.onChange(values);
        })
        .catch(handleApiError);
    } else {
      if (isMinipurchaseUpdateInProgress()) {
        pendingMinipurchaseValuesRef.current = {...pendingMinipurchaseValuesRef.current, ...values};
        return;
      }

      const arg: UpdateMinipurchaseApiArg = {
        interestId: props.interestId,
        body: buildMinipurchaseUpdateBody(miniPurchase, values, tenantCurrency),
      };
      updateMinipurchase(arg)
        .unwrap()
        .then((response) => {
          externalFilterRef.current?.onChange(values);
          flushPendingValues(response);
        })
        .catch(handleApiError);
    }
  };

  // Flush pending values after miniPurchase becomes available (POST completed + interest refetched)
  useEffect(() => {
    if (!miniPurchase?.id) {
      return;
    }

    if (pendingMinipurchaseValuesRef.current) {
      const pendingValues = pendingMinipurchaseValuesRef.current;
      pendingMinipurchaseValuesRef.current = null;
      onMiniPurchaseUpdate(pendingValues);
    }
    // onMiniPurchaseUpdate intentionally omitted: including it would cause an infinite loop since
    // it depends on miniPurchase, which changes when this effect flushes pending values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miniPurchase?.id]);

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    if (!isString(rowId)) {
      showNotification.error(`Missing rowId for action ${actionKey}`);
      return;
    }

    const currentMiniPurchase = miniPurchaseRef.current;

    match(actionKey)
      .with('detail', () => {
        navigate(composePath(vehiclesRoutes.detail, {params: {id: rowId}}));
      })
      .with('removeFromOfferedVehicle', () => {
        if (
          !currentMiniPurchase ||
          !currentMiniPurchase.id ||
          isNil(currentMiniPurchase.offeredVehiclesIds?.find((id) => id === rowId))
        ) {
          showNotification.warning(i18n.t('entity.interest.actions.removeToOfferedVehicleWarning'));
          return;
        }
        const arg: UpdateMinipurchaseApiArg = {
          interestId: props.interestId,
          body: {
            ...currentMiniPurchase,
            minipurchaseId: currentMiniPurchase.id,
            offeredVehiclesIds: currentMiniPurchase.offeredVehiclesIds?.filter(
              (id: string) => id !== rowId
            ),
          },
        };
        updateMinipurchase(arg)
          .unwrap()
          .then(() =>
            showNotification.info(i18n.t('entity.interest.actions.removeToOfferedVehicle'))
          )
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('addToOfferedVehicle', () => {
        if (!currentMiniPurchase || !currentMiniPurchase?.id) {
          const arg: CreateMinipurchaseApiArg = {
            body: {
              interestId: props.interestId,
              offeredVehiclesIds: [rowId],
            },
          };
          createMinipurchase(arg)
            .unwrap()
            .then(() => {
              showNotification.info(i18n.t('entity.interest.actions.addToOfferedVehicle'));
              refreshData();
            })
            .catch(handleApiError);
        } else {
          const vehicleId = rowId;

          addMinipurchaseOfferedVehicle({
            minipurchaseId: currentMiniPurchase.id,
            addMinipurchaseOfferedVehicleRequestBody: {
              vehicleId,
            },
          })
            .unwrap()
            .then(() => {
              showNotification.info(i18n.t('entity.interest.actions.addToOfferedVehicle'));
              refreshData();
            })
            .catch(handleApiError);
        }
      })
      .otherwise(() => {
        showNotification.warning(`Action callback was not specified for action ${actionKey}`);
      });
  };

  const handleOpenEmailLeadPreviewDialog = () => {
    openDialog(<EmailLeadPreviewDialog message={aiLeadEmailMessage!} />, {
      id: 'emailLeadPreviewDialog',
      size: 'large',
      title: i18n.t('entity.interest.aiLead.sentEmail'),
    });
  };

  return (
    <Section
      data-testid={testIds.interest.detail('selling')}
      actions={[
        {
          type: 'button',
          variant: 'outlined',
          onClick: () =>
            navigate(composePath(interestRoutes.detailOverview, {params: {id: props.interestId}})),
          'data-testid': testIds.interest.detail('sellingContinue'),
          isDisabled: !miniPurchase || isActionsDisabled,
          title: i18n.t('general.actions.continue'),
        },
      ]}
      tabs={[
        {
          title: i18n.t('entity.interest.labels.requestSpecification'),
          queryId: interestSearchParams.interestDetail.sellingTabs.request,
          content: (
            <DataStatus isLoading={isLoading} isError={isError} minHeight={175}>
              {interest && (
                <>
                  <VStack spacing={4}>
                    <Show whenFeatureEnabled={featureFlags.INTEREST_CUSTOMER_INQUIRY}>
                      <Card title={i18n.t('entity.interest.aiLead.customerInquiry')}>
                        <VStack spacing={1}>
                          <HStack width="100%" justify="space-between" align="flex-end">
                            <Label>{i18n.t('entity.interest.aiLead.originalInquiry')}</Label>
                            <Button
                              size="small"
                              variant="link"
                              title={i18n.t('entity.interest.aiLead.showOriginal')}
                              onClick={() => handleOpenEmailLeadPreviewDialog()}
                              isDisabled={isNilOrEmpty(aiLeadEmailMessage)}
                            />
                          </HStack>
                          <Box
                            padding={4}
                            border="1px solid"
                            borderColor="palettes.neutral.40.100"
                            backgroundColor="palettes.neutral.10.100"
                            borderRadius="medium"
                          >
                            <MessageText htmlData={aiLeadEmailMessage?.body ?? EMPTY_PLACEHOLDER} />
                          </Box>
                        </VStack>
                      </Card>
                    </Show>
                    <Form<ExternalFilter>
                      data-testid={testIds.interest.detail('requestSpecification')}
                      defaultValues={transformMinipurchaseToExternalFilter(miniPurchase)}
                      isDisabled={!hasChangeMinisalePriceOfferedInterestPermission}
                    >
                      {(control, formApi) => (
                        <Card
                          isExpandable
                          data-testid={testIds.interest.detail('requestSpecification')}
                          title={i18n.t('entity.interest.labels.requestSpecification')}
                          actions={[
                            {
                              variant: 'link',
                              type: 'button',
                              leftIcon: 'action/delete',
                              isLoading,
                              isDisabled:
                                interest?.state === 'CLOSED' ||
                                interest?.state === 'UNSUCCESSFUL' ||
                                !hasChangeMinisalePriceOfferedInterestPermission,
                              onClick: () => {
                                handleFormReset<ExternalFilter>(formApi, [
                                  {name: 'type', value: ['VEHICLETYPE_PASSENGER_CAR']},
                                  {name: 'make', value: null},
                                  {name: 'modelFamily', value: null},
                                  {name: 'fuelType', value: null},
                                  {name: 'transmission', value: null},
                                  {name: 'bodyStyle', value: null},
                                  {name: 'drive', value: null},
                                  {name: 'features', value: null},
                                  {name: 'vatDeductible', value: null},
                                  {name: 'bodyColor', value: null},
                                  {name: 'labelIds', value: null},
                                  {name: 'salePresetId', value: null},
                                  {name: 'cubicCapacityFrom', value: null},
                                  {name: 'cubicCapacityTo', value: null},
                                  {name: 'priceFrom', value: null},
                                  {name: 'priceTo', value: null},
                                  {name: 'yearFrom', value: null},
                                  {name: 'yearTo', value: null},
                                  {name: 'mileageFrom', value: null},
                                  {name: 'mileageTo', value: null},
                                  {name: 'powerFrom', value: null},
                                  {name: 'powerTo', value: null},
                                  {name: 'additionalInformation', value: null},
                                ]);
                                onMiniPurchaseUpdate(formApi.getValues());
                              },
                              'data-testid': testIds.interest.detail('resetRequestSpecification'),
                              title: i18n.t('entity.interest.actions.resetSpecification'),
                            },
                          ]}
                        >
                          <Box minHeight={125}>
                            <AutoSave<ExternalFilter> onSave={onMiniPurchaseUpdate} />
                            <ExternalFilterForm
                              data-testid={testIds.interest.detail('requestSpecification')}
                              control={control}
                              formApi={formApi}
                              onChange={onMiniPurchaseUpdate}
                            />
                            <div id={EXTERNAL_FILTER_ID} />
                          </Box>
                        </Card>
                      )}
                    </Form>
                    <Box height={175}>
                      <DmsDataGrid
                        data-testid={testIds.interest.detail('selling')}
                        gridCode="interest-vehicle-list"
                        filterComponent={(params) => (
                          <ExternalFilterConnector
                            ref={externalFilterRef}
                            onDataGridFilterChange={params.onChange}
                          />
                        )}
                        actionCallback={actionCallback}
                        externalFilterId={EXTERNAL_FILTER_ID}
                      />
                    </Box>
                  </VStack>
                </>
              )}
            </DataStatus>
          ),
        },
        {
          title: `${i18n.t('entity.interest.labels.offeredVehicles')} (${offeredVehiclesList?.length ?? 0})`,
          isDisabled: isNilOrEmpty(offeredVehiclesList),
          queryId: interestSearchParams.interestDetail.sellingTabs.offeredVehicles,
          content: (
            <OfferedVehicles
              data-testid={testIds.interest.detail('selling')}
              interestId={props.interestId}
            />
          ),
        },
      ]}
    />
  );
}

const EXTERNAL_FILTER_ID = 'interest-vehicle-list-external-filter';
